import React from 'react';
import { usePricing } from '../contexts/PricingContext';
import { useGuaranteedHires } from '../hooks/useGuaranteedHires';
interface GuaranteeCardProps {
  /** "large" for the Partnership deck, "compact" for the glance sheet. */
  size?: 'large' | 'compact';
}

/**
 * The guarantee card flips on click to reveal both guarantees. It is a
 * presenter-only interaction: no cursor change, hover state, or focus ring
 * hints that it can be clicked.
 */
export function GuaranteeCard({
  size = 'large'
}: GuaranteeCardProps) {
  // Shared, so revealing the guarantee anywhere also surfaces it on the
  // value page's comparison.
  const {
    guaranteeRevealed: flipped,
    setGuaranteeRevealed
  } = usePricing();
  const guaranteedHires = useGuaranteedHires();
  const setFlipped = (update: (current: boolean) => boolean) => setGuaranteeRevealed(update(flipped));
  const large = size === 'large';
  const pad = large ? 'p-10' : 'p-5';
  const radius = large ? 'rounded-2xl' : 'rounded-xl';
  const eyebrow = large ? 'text-sm tracking-widest mb-5' : 'text-[10px] font-bold tracking-widest mb-2';
  const headline = large ? 'text-3xl md:text-4xl font-bold leading-snug' : 'text-lg font-bold leading-snug';
  const body = large ? 'text-lg leading-relaxed' : 'text-xs leading-relaxed';
  const face = `[grid-area:1/1] [backface-visibility:hidden] bg-slate-900/40 border border-[#7FE0C3]/40 ${radius} ${pad} relative flex flex-col justify-center`;
  return <div onClick={() => setFlipped(current => !current)} className="h-full [perspective:1600px] select-none" data-id="element-547" data-mp="27zqjgy1mr4wi">
      <div className="grid h-full transition-transform [transform-style:preserve-3d]" style={{
      transitionDuration: '390ms',
      transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)',
      transform: `rotateY(${flipped ? 180 : 0}deg)`
    }} data-id="element-548" data-mp="1tblzlqqucnc5">
        {/* Front */}
        <div aria-hidden={flipped} className={face} data-id="element-549" data-mp="1p7xr95udxnpu">
          {large && <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3] rounded-t-2xl" data-id="element-550" data-mp="3qyoe9e7qruk9" />}
          <div className={`uppercase text-[#7FE0C3] ${eyebrow}`} data-id="element-551" data-mp="3m7g8qt9j8tcd">
            Our Guarantee to You
          </div>
          <p className={`text-white ${headline} ${large ? 'mb-5' : ''}`} data-id="element-552" data-mp="3h9eow64a8pzu">
            100% of our fees back inside the first 30 days of the campaign.
          </p>
          <p className={`text-slate-400 ${body} ${large ? '' : 'mt-2'}`} data-id="element-553" data-mp="drms8ef7bhvb">
            We take the bigger risk on this partnership than you do.
            {large && ' If the system is not working in the first 30 days, you are not paying for it.'}
          </p>
        </div>

        {/* Back */}
        <div aria-hidden={!flipped} className={`${face} [transform:rotateY(180deg)]`} data-id="element-554" data-mp="3vyasnqzb3gun">
          {large && <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3] rounded-t-2xl" data-id="element-555" data-mp="2piqmyrusbgl4" />}
          <div className={`uppercase text-[#7FE0C3] ${eyebrow}`} data-id="element-556" data-mp="2kh1bjb3nh0wi">
            Our Guarantees
          </div>
          <div className={large ? 'space-y-7' : 'space-y-3'} data-id="element-557" data-mp="zy7au2wf2ohb">
            <div data-id="element-558" data-mp="150g1utpqbggs">
              <div className={`uppercase text-slate-500 ${large ? 'text-xs tracking-widest mb-2' : 'text-[10px] tracking-widest mb-1'}`} data-id="element-559" data-mp="14zwozrexri9m">
                Money-back
              </div>
              <p className={`text-white ${headline}`} data-id="element-560" data-mp="1dtt78v4zcajr">
                100% of our fees back inside the first 30 days of the
                campaign.
              </p>
            </div>
            <div className={`border-t border-slate-800 ${large ? 'pt-7' : 'pt-3'}`} data-id="element-561" data-mp="1no1lqex6e2yx">
              <div className={`uppercase text-slate-500 ${large ? 'text-xs tracking-widest mb-2' : 'text-[10px] tracking-widest mb-1'}`} data-id="element-562" data-mp="1smuxo2wtdlwz">
                Performance
              </div>
              <p className={`text-white ${headline}`} data-id="element-563" data-mp="2cjpcd1i1qkk">
                <span className="text-[#7FE0C3]" data-id="element-564" data-mp="2d1sltmkx51z">
                  {guaranteedHires} hires
                </span>{' '}
                within the first 90 days of the campaign, or we work for free
                until we do.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}